"use client";

import {
  useState,
  useRef,
  useEffect,
  Children,
  isValidElement,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import {
  LucideIcon,
  ChevronDown,
  Check,
  AlertCircle,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "onChange"
> {
  label?: string;
  labelClassName?: string;
  error?: string;
  helperText?: string;
  leftIcon?: LucideIcon;
  // Drop-in with the old native <select>: callers use e.target.value
  onChange?: (e: { target: { value: string } }) => void;
}

type Option = { value: string; label: ReactNode; disabled?: boolean };

const Select = ({
  label,
  labelClassName,
  error,
  helperText,
  leftIcon: LeftIcon,
  children,
  className = "",
  id,
  value,
  defaultValue,
  onChange,
  disabled,
}: SelectProps) => {
  const selectId = id || label?.toLowerCase().replace(/\s+/g, "-");
  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState(
    defaultValue !== undefined ? String(defaultValue) : "",
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const current = value !== undefined ? String(value) : internal;

  const options: Option[] = [];
  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === "option") {
      const p = child.props as {
        value?: string | number;
        children?: ReactNode;
        disabled?: boolean;
      };
      options.push({
        value: String(p.value ?? ""),
        label: p.children,
        disabled: p.disabled,
      });
    }
  });

  const selected = options.find((o) => o.value === current);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [open]);

  const select = (v: string) => {
    if (value === undefined) setInternal(v);
    onChange?.({ target: { value: v } });
    setOpen(false);
  };

  // Roving focus among option buttons for listbox keyboard nav
  const moveFocus = (dir: 1 | -1) => {
    const items = listRef.current?.querySelectorAll<HTMLButtonElement>(
      "[role=option]:not([disabled])",
    );
    if (!items || items.length === 0) return;
    const arr = Array.from(items);
    const idx = arr.indexOf(document.activeElement as HTMLButtonElement);
    const next = (idx + dir + arr.length) % arr.length;
    arr[next < 0 ? 0 : next]?.focus();
  };

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={selectId}
          className={cn(
            "block text-[13px] font-medium text-foreground mb-2 tracking-[-0.01em]",
            labelClassName,
          )}
        >
          {label}
        </label>
      )}

      <div className="relative" ref={rootRef}>
        {LeftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-[1]">
            <LeftIcon className="w-4 h-4" />
          </div>
        )}

        <button
          type="button"
          id={selectId}
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              if (!open) setOpen(true);
              else moveFocus(e.key === "ArrowDown" ? 1 : -1);
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={`${selectId}-listbox`}
          aria-describedby={
            error
              ? `${selectId}-error`
              : helperText
                ? `${selectId}-helper`
                : undefined
          }
          className={cn(
            "flex items-center gap-2 w-full cursor-pointer text-left",
            "bg-input text-foreground border border-border rounded-[10px]",
            "px-3 py-2.5 text-sm tracking-[-0.005em]",
            "transition-[border-color,box-shadow] duration-[220ms]",
            "hover:border-bd-hi",
            "disabled:opacity-40 disabled:cursor-not-allowed",
            open &&
              "border-brand-glow-60 [box-shadow:var(--input-focus-ring)] hover:border-brand-glow-60",
            error &&
              "border-error hover:border-error [box-shadow:var(--input-error-ring)]",
            LeftIcon && "pl-[38px]",
            className,
          )}
        >
          <span
            className={cn(
              "flex-1 truncate",
              !selected && "text-muted-foreground",
            )}
          >
            {selected ? selected.label : (options[0]?.label ?? "")}
          </span>
          <ChevronDown
            className={cn(
              "w-4 h-4 shrink-0 text-muted-foreground transition-transform duration-[220ms]",
              open && "rotate-180 text-brand",
            )}
          />
        </button>

        {open && (
          <div
            ref={listRef}
            id={`${selectId}-listbox`}
            role="listbox"
            aria-label={label}
            className="absolute top-[calc(100%+8px)] left-0 right-0 z-[25] bg-card border border-bd-hi rounded-xl p-2 [box-shadow:var(--modal-shadow)] max-h-64 overflow-auto"
          >
            {options.map((o) => {
              const on = o.value === current;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={on}
                  disabled={o.disabled}
                  onClick={() => select(o.value)}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                      e.preventDefault();
                      moveFocus(e.key === "ArrowDown" ? 1 : -1);
                    } else if (e.key === "Escape") {
                      setOpen(false);
                    }
                  }}
                  className={cn(
                    "flex items-center justify-between gap-2 w-full text-left",
                    "px-2.5 py-2.5 rounded-lg text-[13px]",
                    "transition-colors duration-[220ms]",
                    "hover:bg-card-hover hover:text-foreground",
                    "disabled:opacity-40 disabled:cursor-not-allowed",
                    on ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  <span className="truncate">{o.label}</span>
                  {on && (
                    <Check className="w-[15px] h-[15px] shrink-0 text-brand" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {error && (
        <p
          id={`${selectId}-error`}
          className="mt-2 text-xs text-error inline-flex items-center gap-1.5"
        >
          <AlertCircle className="w-3 h-3 shrink-0" aria-hidden />
          {error}
        </p>
      )}

      {helperText && !error && (
        <p
          id={`${selectId}-helper`}
          className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1.5"
        >
          <Info className="w-3 h-3 shrink-0" aria-hidden />
          {helperText}
        </p>
      )}
    </div>
  );
};

Select.displayName = "Select";

export { Select, type SelectProps };
